import { DialogService } from 'aurelia-dialog';
import { bindable } from 'aurelia-framework';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';
import {
  ComponentTemplateType,
  CreateTemplateDialogArgs,
  MooringCreateUpdateTemplateDialog,
} from 'features/mooring/component-templates/mooring-create-template-dialog';
import {
  MooringSelectEntriesForPupulateArgs,
  MooringSelectEntriesForPupulateDialog,
  MooringSelectEntriesForPupulateResult,
} from 'features/mooring/component-templates/mooring-select-entries-for-populate';
import {
  MooringSelectTemplateDialog,
  SelectTemplateDialogArgs,
  SelectTemplateDialogResult,
} from 'features/mooring/component-templates/mooring-select-template-dialog';
import { MooringArticleFinderDialog, MooringArticleFinderDialogResult } from 'features/mooring/mooring-article-finder-dialog';
import { MooringComponentCommentDialog, MooringComponentCommentDialogArgs } from 'features/mooring/mooring-component-comment-dialog';
import { PubSub } from 'lib/event/PubSub';
import { EqualityChecker, Keyboard } from 'lib/helpers';
import { AsyncErrorHandler } from 'lib/ui';
import { InputType } from 'models/ListSettings';
import {
  MooringArticle,
  MooringComponent,
  MooringElementsListDto,
  MooringComponentType,
  MooringComponentTemplateEntryDto,
} from 'models/mooring';
import { MaterialFactor } from 'models/mooring/MaterialFactor';
import { MooringArticleService } from 'services';
import { MaterialFactorService } from 'services/material-factor-service';
import { MooringComponentSearchHandler, MooringOfferService, MooringService } from 'services/mooring';
import { TableEntryManager } from 'services/ui-helpers';

export abstract class MooringTableBase<T extends { Id: number; MoorlogNo: string; Components: MooringComponent[] }> {
  @bindable
  protected mooringId!: number;

  @bindable
  protected offerId!: number;

  @bindable
  protected entries!: T[];
  entriesChanged(newValue: T[]) {
    if (!newValue?.length) return;
    this.changeTracker = new EqualityChecker();
    this.changeTracker.track(newValue);
  }

  protected mooringElements!: MooringElementsListDto;

  protected changeTracker = new EqualityChecker();

  protected articles!: MooringArticle[];

  protected editRows = false;

  protected openBoxes: { [key: string]: boolean } = {};

  protected loadUnitTypeOnPaste: 'tons' | 'kg' = 'tons';

  protected get defaultComponent() {
    return {
      Id: 0,
      UtilizationFactor: 0,
      HasLoadConstraint: true,
      ArticleId: 0,
      Quantity: 1,
      TypeName: '',
      CategoryName: '',
      ProductName: '',
      SortIndex: 0,
    };
  }

  /**
   *
   */
  constructor(
    protected type: ComponentTemplateType,
    protected dialogService: DialogService,
    protected mooringComponentSearchHandler: MooringComponentSearchHandler,
    protected tableEntryManager: TableEntryManager,
    protected mooringArticleService: MooringArticleService,
    protected pubsub: PubSub,
    protected confirmation: ConfirmDialog,
    protected mooringOfferService: MooringOfferService,
    protected mooringService: MooringService,
    protected materialFactorService: MaterialFactorService,
    protected materialFactors: MaterialFactor
  ) {}

  _tableScrollHandler!: () => void;
  _tableResizeHandler!: () => void;

  private attachStickyHeaderHandler() {
    const fixedHeader = document.getElementById('table-fixed-header');
    const tableHeader = document.getElementById('table-main-header');
    if (!fixedHeader || !tableHeader) return;

    // Match the width of the table header to the fixed header
    this._tableResizeHandler = () => {
      const tableCells = tableHeader.querySelectorAll('th');
      const fixedCells = fixedHeader.querySelectorAll('th');

      fixedCells.forEach((cell, index) => {
        cell.style.width = `${tableCells[index].offsetWidth}px`;
      });
    };

    this._tableScrollHandler = () => {
      const rect = tableHeader.getBoundingClientRect();
      // Check if the table header is out of view
      if (rect.top <= 0) {
        fixedHeader.classList.remove('hidden');
        fixedHeader.style.width = `${rect.width}px`;
        this._tableResizeHandler();
      } else {
        fixedHeader.classList.add('hidden');
      }
    };

    window.addEventListener('scroll', this._tableScrollHandler);
    window.addEventListener('resize', this._tableResizeHandler);
  }

  async attached() {
    await this.getMaterialFactors();
    await this.getMooringElements();
    await this.prePolulateSearchService();
    this.attachStickyHeaderHandler();
  }

  async prePolulateSearchService() {
    // this.articles = await this.mooringArticleService.getAll('$expand=ArticleProducts($expand=Product)');

    // this.articles[0].ArticleProducts[0].pr
    // const ids = Array.from(new Set(this.entries.flatMap((x) => x.Components.map((e) => e.ArticleId))));
    await this.mooringComponentSearchHandler.preCache();
  }

  public searchForElement(type: MooringComponentType, term: string) {
    if (type === 'buoy') {
      return this.mooringElements.Buoys.filter((x) => x.Name.toLowerCase().includes(term.toLocaleLowerCase())).map((x) => ({
        id: x.Id,
        title: x.Name,
      }));
    }
    if (type === 'cage') {
      return this.mooringElements.Cages.filter((x) => x.Name.toLowerCase().includes(term.toLocaleLowerCase())).map((x) => ({
        id: x.Id,
        title: x.Name,
      }));
    }
    return [];
  }

  public getElementDetails(type: MooringComponentType, id: number) {
    if (type === 'buoy') {
      const b = this.mooringElements.Buoys.find((x) => x.Id === id);
      return {
        id,
        title: b?.Name,
      };
    }

    if (type === 'cage') {
      const b = this.mooringElements.Cages.find((x) => x.Id === id);
      return {
        id,
        title: b?.Name,
      };
    }
  }

  @AsyncErrorHandler
  protected async getMaterialFactors() {
    const response = await this.materialFactorService.getActiveMaterialFactors();
    this.materialFactors = response.data;
    this.materialFactors.RopeWithKnotAccidentFactor = Number((response.data.RopeWithKnotFactor / 1.5).toFixed(2));
  }

  @AsyncErrorHandler
  protected async getMooringElements() {
    const { data } = await this.mooringService.getMooringElementsList(this.mooringId);
    this.mooringElements = data;
  }

  protected addComponent(lineId: number, index?: number) {
    const line = this.entries.find((x) => x.Id === lineId);
    if (!line) return;
    if (!line.Components) line.Components = [];
    line.Components = this.tableEntryManager.addEntry(line.Components, this.defaultComponent, index);
  }

  protected removeComponent(lineId: number, index: number) {
    const line = this.entries.find((x) => x.Id === lineId);
    if (!line) return;
    line.Components = this.tableEntryManager.removeEntry(line.Components, index);
  }

  protected moveComponentUp(lineId: number, index: number) {
    const line = this.entries.find((x) => x.Id === lineId);
    if (!line) return;
    line.Components = this.tableEntryManager.moveEntryUp(line.Components, index);
  }

  protected moveComponentDown(lineId: number, index: number) {
    const line = this.entries.find((x) => x.Id === lineId);
    if (!line) return;
    line.Components = this.tableEntryManager.moveEntryDown(line.Components, index);
  }

  protected setEdit() {
    this.editRows = !this.editRows;
  }

  protected openSubTable(id: string | number) {
    this.openBoxes[id] = !this.openBoxes[id];
    this.openBoxes = { ...this.openBoxes };
  }

  protected contractAllRows() {
    this.openBoxes = {};
  }

  protected expandAllRows() {
    this.entries.forEach((line) => {
      this.openBoxes[line.Id] = true;
    });
  }

  kgAdjust = [
    'DimForce',
    'MblRopes',
    'MblSinkRopes',
    'MblStraps',
    'MblChainShackleConnectionPlates',
    'MblUsedChainRopeWithKnot',
    'MblAnchoring',
    'BottomAnchoringUplift',
    'HoldingPowerAnchoring',
    'MblAdditionalSafety',
    'MblMainStrap',
    'PullDownKg',
    'ResidualDriftKg',
  ];

  protected abstract postPasteAdjustment(_: number, __: string): void;

  protected abstract setComponentQuantity(component: MooringComponent, line: T): void;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected async pasteValue(event: ClipboardEvent, field: string, index: number, _?: InputType) {
    if (!this.entries?.length) return;

    const data = event.clipboardData?.getData('text');
    if (!data) return;
    const lines = data.split('\n').filter(Boolean);

    if (!this.kgAdjust.includes(field)) {
      for (let i = 0; i < lines.length; i++) {
        const entryIndex = index + i;
        if (entryIndex >= this.entries.length) break;
        const entry = this.entries[entryIndex];
        entry[field] = lines[i]?.trim().replace(',', '.').replaceAll(' ', '').replace(/\D\./g, '');

        this.postPasteAdjustment(entryIndex, field);
      }
      return;
    }

    const listOfValues = lines
      .map((item) => item.trim())
      .map((item) => item.replace(/,$/, '').replace(',', '.'))
      .filter((item) => item !== '' && !isNaN(Number(item)))
      .map((item) => Number(item));

    let res = null;
    let confirmationText = null;

    if (this.loadUnitTypeOnPaste === 'tons' && listOfValues.filter((value) => value >= 500).length > 0) {
      confirmationText = 'dialog.areYourSureTon';
    }

    if (this.loadUnitTypeOnPaste === 'kg' && listOfValues.filter((value) => value <= 1000).length > 0) {
      confirmationText = 'dialog.areYourSureKilo';
    }

    if (confirmationText != null) {
      res = await this.confirmation.confirmYesNo('dialog.areYouSure', confirmationText);
    }

    if (res) {
      for (let i = 0; i < lines.length; i++) {
        const entryIndex = index + i;
        if (entryIndex >= this.entries.length) break;

        const entry = this.entries[entryIndex];

        const value = lines[i]?.trim().replace(',', '.').replaceAll(' ', '').replace(/\D\./g, '');
        let numericValue = +value;
        // All load values should be in KG's
        if (this.loadUnitTypeOnPaste === 'tons') {
          numericValue = +(numericValue * 1000).toFixed(2);
        }
        if (Number.isNaN(numericValue)) {
          numericValue = 0;
        }
        entry[field] = numericValue;

        this.postPasteAdjustment(entryIndex, field);
      }
      return;
    }

    if (res === null) {
      for (let i = 0; i < lines.length; i++) {
        const entryIndex = index + i;
        if (entryIndex >= this.entries.length) break;

        const entry = this.entries[entryIndex];

        const value = lines[i]?.trim().replace(',', '.').replaceAll(' ', '').replace(/\D\./g, '');
        let numericValue = +value;
        // All load values should be in KG's
        if (this.loadUnitTypeOnPaste === 'tons') {
          numericValue = +(numericValue * 1000).toFixed(2);
        }
        if (Number.isNaN(numericValue)) {
          numericValue = 0;
        }
        entry[field] = numericValue;

        this.postPasteAdjustment(entryIndex, field);
      }
      return;
    }
  }

  protected saveAsTemplate(lineId: number) {
    const line = this.entries.find((x) => x.Id == lineId);
    if (!line) return;

    void this.dialogService.open({
      viewModel: MooringCreateUpdateTemplateDialog,
      position: () => 0,
      model: {
        action: 'create',
        type: this.type,
        components: line.Components.map((x) => ({
          SortIndex: x.SortIndex,
          ProductName: x.ProductName,
          ArticleProductId: x.ArticleProductId,
          HasLoadConstraint: x.HasLoadConstraint,
          IsForCounterWeight: x.IsForCounterWeight,
          MBL: x.MBL,
          CategoryName: x.CategoryName,
          TypeName: x.TypeName,
          ProductTypeName: x.ProductTypeName,
        })),
      } satisfies CreateTemplateDialogArgs,
    });
  }

  /**
   * Populate line with components.
   * If a line id is provided, only that line will be populated,
   * else all will be.
   */
  protected populate(components: MooringComponentTemplateEntryDto[], lineId?: number) {
    const mooringComponents: MooringComponent[] = components
      .map(
        (x) =>
          ({
            Id: 0,
            // ArticleId: x.ArticleId,
            ArticleProductId: x.ArticleProductId,
            TypeName: x.TypeName,
            Quantity: x.Quantity || 1,
            MBL: x.MBL,
            WLL: x.WLL,
            SortIndex: x.SortIndex,
            ProductName: x.ProductName,
            CategoryName: x.CategoryName,
            HasLoadConstraint: x.HasLoadConstraint,
            HasKnot: x.HasKnot,
            ProductTypeName: x.ProductTypeName,
            IsForCounterWeight: x.IsForCounterWeight,
            UtilizationFactor: 0,
            CategoryType: x.CategoryType,
            TypeVariant: x.TypeVariant,
            PrimaryDimension: x.PrimaryDimension,
            SecondaryDimension: x.SecondaryDimension,
          }) satisfies MooringComponent
      )
      .sort((a, b) => a.SortIndex - b.SortIndex);

    if (!lineId) {
      for (const line of this.entries) {
        if (!line.Components) {
          line.Components = [];
        }
        line.Components.push(...mooringComponents);
        this.tableEntryManager.organizeSortIndex(line.Components);

        mooringComponents.forEach((c) => {
          this.setComponentQuantity(c, line);
        });
      }
    } else {
      const line = this.entries.find((x) => x.Id == lineId);
      line.Components?.push(...mooringComponents);

      mooringComponents.forEach((c) => {
        this.setComponentQuantity(c, line);
      });

      this.tableEntryManager.organizeSortIndex(line.Components);
    }
  }

  protected abstract getEntriesForPopulate(): MooringSelectEntriesForPupulateArgs;

  protected selectArticleToPopulateLines() {
    void this.dialogService
      .open({
        viewModel: MooringArticleFinderDialog,
        position: () => 0,
      })
      .whenClosed(({ output }: { output?: MooringArticleFinderDialogResult }) => {
        if (!output?.component) return;
        const component = output.component;

        void this.dialogService
          .open({
            viewModel: MooringSelectEntriesForPupulateDialog,
            position: () => 0,
            model: this.getEntriesForPopulate(),
          })
          .whenClosed(({ output }: { output?: MooringSelectEntriesForPupulateResult }) => {
            if (!output) return;

            for (const selection of output.selections) {
              if (selection.selected) {
                this.populate([component], +selection.id);
              }
            }
          });
      });
  }

  protected selecteTemplateToPopulateLines(entryId?: number) {
    // Cages does not have template
    if (this.type === 'cage') return;

    void this.dialogService
      .open({
        viewModel: MooringSelectTemplateDialog,
        position: () => 0,
        model: {
          type: this.type,
        } satisfies SelectTemplateDialogArgs,
      })
      .whenClosed(({ output }: { output?: SelectTemplateDialogResult }) => {
        if (!output) return;
        const template = output.template;

        if (!entryId) {
          void this.dialogService
            .open({
              viewModel: MooringSelectEntriesForPupulateDialog,
              position: () => 0,
              model: this.getEntriesForPopulate(),
            })
            .whenClosed(({ output }: { output?: MooringSelectEntriesForPupulateResult }) => {
              if (!output) return;

              for (const selection of output.selections) {
                if (selection.selected) {
                  this.populate(template.Components ?? [], +selection.id);
                }
              }
            });
        } else {
          this.populate(template.Components ?? [], entryId);
        }
      });
  }

  /**
   * Handles the Enter key to navigate to the next line in the table.
   * If Shift + Enter is pressed, it navigates to the previous line.
   * field is the name of the field.
   * @param event The keyboard event.
   * @param parentIndex The index of the outer table row, not the article subtable index.
   * @param field The field name to identify the property/input.
   */
  protected tryGotoNextLine(event: KeyboardEvent, parentIndex: number, field: string) {
    const isEnterKey = Keyboard.isEnterKey(event);
    const isShiftEnter = Keyboard.isShiftEnterKey(event);

    if (!isEnterKey && !isShiftEnter) {
      return;
    }

    event.preventDefault();

    let nextIndex = isShiftEnter ? parentIndex - 1 : parentIndex + 1;
    nextIndex = Math.max(0, Math.min(this.entries.length - 1, nextIndex));

    const tableBody = document.getElementById('components-table-body');

    if (!tableBody) {
      return;
    }

    const nextRow = document.querySelector(`[data-identifier="${field}-${nextIndex}"]`);

    if (!nextRow) {
      return;
    }

    nextRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
    (nextRow as HTMLInputElement | HTMLTextAreaElement | null)?.focus();
  }

  protected duplicate(entryIndex: number, componentIndex: number) {
    const entry = this.entries[entryIndex];
    if (!entry) return;

    const original = entry.Components[componentIndex];
    const newEntry = new MooringComponent();
    Object.assign(newEntry, original);
    newEntry.Id = undefined;

    entry.Components = this.tableEntryManager.addEntry(entry.Components, newEntry, componentIndex);
  }

  protected scrollToTop() {
    document.querySelector('.app__header')?.scrollIntoView({ behavior: 'smooth' });
  }

  protected openCommentDialog(model: MooringComponentCommentDialogArgs) {
    void this.dialogService
      .open({
        viewModel: MooringComponentCommentDialog,
        position: () => 0,
        model,
      })
      .whenClosed(() => {
        this.pubsub.publish('list-entity:updated', null);
      });
  }

  protected unbind() {
    window.removeEventListener('scroll', this._tableScrollHandler);
    window.removeEventListener('resize', this._tableResizeHandler);
  }
}
